import { AxiosError } from 'axios';

type ProblemPayload = {
  detail?: string;
  title?: string;
  errors?: Record<string, string[] | string>;
};

function firstValidationError(errors: Record<string, string[] | string>): string | undefined {
  for (const value of Object.values(errors)) {
    const message = Array.isArray(value) ? value.find((item) => item.trim()) : value;
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
  }

  return undefined;
}

function looksLikeHtmlDocument(value: string): boolean {
  const trimmed = value.trimStart().toLowerCase();
  return trimmed.startsWith('<!doctype html') || trimmed.startsWith('<html');
}

function messageFromGatewayHtml(html: string, status?: number): string | undefined {
  const titleMatch = html.match(/<title>\s*([^<]+)\s*<\/title>/i);
  const title = titleMatch?.[1]?.trim() ?? '';
  if (/504|gateway time-?out/i.test(title) || status === 504) {
    return 'O servidor demorou demais para responder (timeout). Tente novamente em instantes.';
  }

  if (/502|bad gateway/i.test(title) || status === 502) {
    return 'Serviço temporariamente indisponível (bad gateway). Tente novamente.';
  }

  if (/503|service unavailable/i.test(title) || status === 503) {
    return 'Serviço temporariamente indisponível. Tente novamente.';
  }

  if (status && status >= 500) {
    return 'Falha temporária no servidor. Tente novamente.';
  }

  return undefined;
}

export function getRequestErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const data = error.response?.data as ProblemPayload | string | undefined;

    if (typeof data === 'string' && data.trim()) {
      if (looksLikeHtmlDocument(data)) {
        return messageFromGatewayHtml(data, status) ?? fallback;
      }

      return data.trim();
    }

    if (data && typeof data === 'object') {
      if (typeof data.detail === 'string' && data.detail.trim()) {
        if (looksLikeHtmlDocument(data.detail)) {
          return messageFromGatewayHtml(data.detail, status) ?? fallback;
        }

        return data.detail.trim();
      }

      if (data.errors) {
        const validation = firstValidationError(data.errors);
        if (validation) {
          if (validation.includes('could not be converted') || validation.includes('JSON value')) {
            return 'Há um campo inválido no canal (empresa, equipe ou SLA). Recarregue e tente de novo.';
          }

          return validation;
        }
      }
    }

    if (status === 504) {
      return 'O servidor demorou demais para responder (timeout). Tente novamente em instantes.';
    }
  }

  return fallback;
}
